import stampit from 'stampit';
import {
  pipe, reverse, replace, flip, concat, ifElse, always, curry, toUpper,
} from 'ramda';
import { isNullish } from './Utils';

/* Coerce */
// Removes every character in a string that isn't a letter
const toAlphabet = stampit({
  methods: {
    coerce(inputString) {
      return inputString?.replace(/[^a-zA-Z]/g, '');
    },
  },
});
const toAlphaNumeric = stampit({
  methods: {
    coerce(inputString) {
      return inputString?.replace(/[^a-zA-Z0-9]/g, '');
    },
  },
});

const toAddress = stampit({
  methods: {
    coerce(inputString) {
      return inputString?.replace(/[^a-zA-Z0-9 #-]/g, '');
    },
  },
});

const toName = stampit({
  methods: {
    coerce(inputString) {
      return inputString
        ?.replace(/[^a-zA-Z\u00C0-\u017F ]/g, '')
        ?.replace(/\s\s+/g, ' ');
    },
  },
});

// Removes every character in a string that isn't a digit
const toDigits = stampit({
  methods: {
    coerce(inputString) {
      return inputString?.replace(/[^0-9]/g, '');
    },
  },
});

const toUpperCase = stampit({
  methods: {
    coerce(inputString) {
      return toUpper(inputString);
    },
  },
});

const toReadableNatural = stampit({
  props: {
    separator: '',
  },
  methods: {
    coerce(inputString) {
      return inputString?.replace(new RegExp(`[^0-9${this.separator}]`, 'g'), '');
    },
  },
});

const toNumeric = stampit({
  props: {
    separator: '',
  },
  methods: {
    // Removes all non-digits in a string
    coerce(inputString) {
      return inputString?.replace(/\D/g, '');
    },
    // Tries (empty string fail) to parse as an integer
    parse(inputString) {
      const digits = inputString?.replace(/\D/g, '');
      const int = parseInt(digits, 10);
      return Number.isNaN(int) ? '' : int;
    },
  },
});

const limitDigits = stampit({
  props: {
    noDigits: 9,
  },
  init({ noDigits, maxValue } = {}) {
    this.noDigits = noDigits ?? (maxValue ? `${maxValue}`.length : this.noDigits);
  },
  methods: {
    coerce(inputString, previousInputString) {
      return pipe(
        v => toDigits.compose.methods.coerce?.call(this, v, previousInputString),
        v => (v.length <= this.noDigits ? inputString : previousInputString),
        v => toReadableNatural.compose.methods.coerce?.call(this, v, previousInputString),
      )(inputString ?? '');
    },
  },
});

const coerce = {
  toAlphabet,
  toAlphaNumeric,
  toDigits,
  limitDigits,
  toReadableNatural,
  toNumeric,
  toUpperCase,
  toName,
  toAddress,
};

/* Parse */
const toNatural = stampit({
  props: {
    radix: 10,
  },
  init({ radix } = {}) {
    this.radix = radix ?? this.radix;
  },
  methods: {
    parse(inputString) {
      const digits = inputString.replace(/[^0-9]/g, '');
      return parseInt(digits, 10);
    },
  },
});

const parse = {
  toNatural,
  toNumeric,
};

/* Normalize */
const normalize = {

};

/* Finalize */
const min = stampit({
  props: {
    minValue: 0,
    autoCorrect: false,
  },
  init({ minValue, autoCorrect } = {}) {
    this.minValue = minValue ?? this.minValue;
    this.autoCorrect = autoCorrect ?? this.autoCorrect;
  },
  methods: {
    /**
     * Finalize the value to be at least minValue
     * @param {number} normalizedValue
     * @returns minValue if normalizedValue is not a number and autoCorrect is true,
     * otherwise skip finalizing
     */
    finalize(normalizedValue) {
      return pipe(
        parseInt,
        ifElse(
          Number.isNaN,
          ifElse(
            always(this.autoCorrect),
            always(this.minValue),
            always(null),
          ),
          curry(Math.max)(this.minValue),
        ),
      )(normalizedValue);
    },
  },
});

const max = stampit({
  props: {
    maxValue: 999999999,
    autoCorrect: false,
  },
  init({ maxValue, autoCorrect } = {}) {
    this.maxValue = maxValue ?? this.maxValue;
    this.autoCorrect = autoCorrect ?? this.autoCorrect;
  },
  methods: {
    /**
     * Finalize the value to be at most maxValue
     * @param {number} normalizedValue
     * @returns maxValue if normalizedValue is not a number and autoCorrect is true,
     * otherwise skip finalizing
     */
    finalize(normalizedValue) {
      return pipe(
        parseInt,
        ifElse(
          Number.isNaN,
          ifElse(
            always(this.autoCorrect),
            always(this.maxValue),
            always(null),
          ),
          curry(Math.min)(this.maxValue),
        ),
      )(normalizedValue);
    },
  },
});

const within = stampit({
  props: {
    minValue: 0,
    maxValue: 999999999,
    autoCorrect: false,
  },
  init({ minValue, maxValue, autoCorrect } = {}) {
    this.minValue = minValue ?? this.minValue;
    this.maxValue = maxValue ?? this.maxValue;
    this.autoCorrect = autoCorrect ?? this.autoCorrect;
  },
  methods: {
    finalize(normalizedValue) {
      return pipe(
        v => min.compose.methods.finalize?.call(this, v),
        v => max.compose.methods.finalize?.call(this, v),
      )(normalizedValue);
    },
  },
});

const finalize = {
  min,
  max,
  within,
};

/* --== Format ==--
 * - Add a stampit constructor below (follow other examples) that contains your
 *   properties that you may want to hand over, an init for null checking, and
 *   the logic involved
 * - Declare this in the `const format` object underneath
 * - Use it in the `const patterns` object
 * - In the JSX file, refer to this pattern via the behaviours property as a
 *   function, e.g. <Input behaviours={ patterns.somePattern({ foo: 42 }) }>
 *   (properties declared here should match the stampit's `props` property)
 */

const formatOptionValue = stampit({
  props: {
    options: {},
  },
  init({ options } = {}) {
    this.options = options ?? this.options;
  },
  methods: {
    format(hash) {
      if (this.options.dropdownOptions == null || hash == null) {
        return '';
      }
      let displayValue = '';
      this.options.dropdownOptions.forEach((option) => {
        if (option.value === hash) {
          displayValue = option.label;
        }
      });
      return displayValue;
    },
  },
});

const thousandsSeparated = stampit({
  props: {
    separator: ',',
  },
  init({ separator } = {}) {
    this.separator = separator ?? this.separator;
  },
  methods: {
    /**
     * Format an integer into a readable thousands-separated number
     * @param {number|string} integer
     * @returns {string} empty string if not an integer
     */
    format(integer) {
      if (Number.isNaN(parseInt(integer, 10))) return '';
      return pipe(
        reverse,
        replace(/.{3}/g, flip(concat)(this.separator)),
        replace(new RegExp(`(^[${this.separator}]|[${this.separator}]$)`, 'g'), ''),
        reverse,
      )(`${integer}`);
    },
  },
});

const sgPhone = stampit({
  props: {
    separator: ' ',
  },
  init({ separator } = {}) {
    this.separator = separator ?? this.separator;
  },
  methods: {
    /**
     * Format an integer into a readable thousands-separated number
     * @param {number|string} integer
     * @returns {string} empty string if not an integer
     */
    format(integer) {
      if (String.toString(integer).length < 4) return integer;
      return pipe(
        replace(/.{4}/g, flip(concat)(this.separator)),
        replace(new RegExp(`(^[${this.separator}]|[${this.separator}]$)`, 'g'), ''),
      )(`${integer}`);
    },
  },
});

const hkPhone = stampit({
  props: {
    separator: ' ',
  },
  init({ separator } = {}) {
    this.separator = separator ?? this.separator;
  },
  methods: {
    /**
     * Format an integer into a readable thousands-separated number
     * @param {number|string} integer
     * @returns {string} empty string if not an integer
     */
    format(integer) {
      if (String.toString(integer).length < 4) return integer;
      return pipe(
        replace(/.{4}/g, flip(concat)(this.separator)),
        replace(new RegExp(`(^[${this.separator}]|[${this.separator}]$)`, 'g'), ''),
      )(`${integer}`);
    },
  },
});

const formatDecimal = stampit({
  props: {
    // Assumes separator and decimal point are single characters, since slice(-1) is used.
    separator: ',',
    options: {},
  },
  init({ separator, options } = {}) {
    this.separator = separator ?? this.separator;
    this.options = options ?? this.options;
  },
  methods: {
    /**
     * An extension of thousandsSeparated, returns a comma separated decimal point number.
     * @param {number|string} value
     * @returns {string} empty string, if the value isn't a number.
     */
    format(val) {
      if (isNullish(val)) return '';
      const dp = this.options.decimalPlaces;
      const value = Number(val);
      if (Number.isInteger(value)) {
        return `${thousandsSeparated().format(value)}${dp ? `.${'0'.repeat(dp)}` : ''}`;
      }
      if (typeof value === 'number') {
        const number = value.toString().split('.');
        const characteristic = thousandsSeparated().format(number[0]);
        const mantissa = number[1];
        if (characteristic !== '' && mantissa !== '') {
          return `${characteristic}.${dp ? mantissa.padEnd(dp, '0').substr(0, dp) : mantissa.substr(0, dp)}`;
        }
      }
      return '';
    },
  },
});

const formatMultiple = stampit({
  props: {
    options: {},
  },
  init({ options } = {}) {
    this.options = options ?? this.options;
  },
  methods: {
    format(input) {
      if (Array.isArray(input)) {
        let output = '';
        for (let i = 0; i < input.length; i++) {
          const valueFormat = this.options?.items[i].valueFormat;
          const options = this.options.items[i];
          // eslint-disable-next-line
          const value = valueFormat ? format[valueFormat]({ options }).format(input[i]) : input[i];
          const prefix = this.options?.items[i]?.prefix ?? '';
          const suffix = this.options?.items[i]?.suffix ?? '';
          output += prefix + value + suffix;
        }
        return output;
      }
      // Also handle single value being passed
      // Prefer showing blank value instead of NaN
      if (Number.isNaN(input)) return '';
      return input;
    },
  },
});

const formatDuration = stampit({
  props: {
    options: {},
  },
  init({ options } = {}) {
    this.options = options ?? this.options;
  },
  methods: {
    format(duration) {
      if (Number.isNaN(parseInt(duration, 10)) && duration < 0) return '';
      return `${formatOptionValue({ options: this.options }).format(duration)}`;
    },
  },
});

const asUpperCase = stampit({
  props: {
    options: {},
  },
  init({ options } = {}) {
    this.options = options ?? this.options;
  },
  methods: {
    format(input) {
      return input ? String(input).toUpperCase() : '';
    },
  },
});

const formatCardOption = stampit({
  props: {
    options: [],
    separator: '-',
  },
  init({ options, separator } = {}) {
    this.options = options ?? this.options;
    this.separator = separator ?? this.separator;
  },
  methods: {
    format(cardOption) {
      const cardString = formatOptionValue({ options: this.options }).format(cardOption);
      // formatOptionValue returns an empty string, not null, if the hash is null
      if (cardString === '') return '';
      let result = '';
      switch (cardString.length) {
        case 16:
          for (let i = 0; i < 4; i++) {
            result += cardString.substr(i * 4, 4) + (i !== 3 ? this.separator : '');
          }
          break;
        default:
          return cardString;
      }
      return result;
    },
  },
});

const formatDate = stampit({
  props: {
    inputSeparator: '-',
    outputSeparator: '/',
  },
  init ({ options } = {}) {
    this.inputSeparator = options?.inputSeparator ?? this.inputSeparator;
    this.outputSeparator = options?.outputSeparator ?? this.outputSeparator;
  },
  methods: {
    // Expects a date in the format year-month-day to return day/month/year
    format(givenDate) {
      if (!givenDate) return '';
      const dates = givenDate.split(this.inputSeparator);
      return `${dates[2]}${this.outputSeparator}${dates[1]}${this.outputSeparator}${dates[0]}`;
    },
  },
});

const format = {
  formatOptionValue,
  thousandsSeparated,
  sgPhone,
  hkPhone,
  formatDecimal,
  formatMultiple,
  formatDuration,
  formatCardOption,
  formatDate,
  asUpperCase,
};

const patterns = {
  readableNaturalRange: stampit(
    coerce.limitDigits,
    parse.toNatural,
    finalize.within,
    format.thousandsSeparated,
  ),
  naturalRange: stampit(
    coerce.toDigits,
    parse.toNatural,
    finalize.within,
  ),
  numeric: stampit(
    coerce.toNumeric,
    parse.toNumeric,
  ),
  digit: stampit(
    coerce.toDigits,
  ),
  alpha: stampit(
    coerce.toAlphabet,
  ),
  address: stampit(
    coerce.toAddress,
  ),
  name: stampit(
    coerce.toName,
  ),
  currency: stampit(
    coerce.toNumeric,
    parse.toNumeric,
    finalize.within,
    format.thousandsSeparated,
  ),
  documentNumber: stampit(
    coerce.toUpperCase,
  ),
  sgDocumentNumber: stampit(
    coerce.toAlphaNumeric,
    format.asUpperCase,
  ),
  sgPhone: stampit(
    coerce.toNumeric,
    parse.toNumeric,
    finalize.within,
    format.sgPhone,
  ),
  hkPhone: stampit(
    coerce.toNumeric,
    parse.toNumeric,
    finalize.within,
    format.hkPhone,
  ),
};

/* Shared code + helpers */

/**
 * Executes a specified formatter function given the value plus any additional
 * options (can be any arbitrary data).
 * @param value         The value of the field, for instance, the `value` or
 *                      `dynamicValue` properties in `formState`.
 * @param valueFormat   The name of the formatter function that is to be called,
 *                      which is specified in this file.
 * @param options       An object for additional data that may be required (e.g.
 *                      the options list when converting hashes to labels)
 * @returns {string}
 */
export const executeFormat = ({
  placeholder,
  value,
  valueFormat,
  options,
}) => {
  let output = '';
  if (options?.prefix) {
    output += `${options.prefix}`;
  }
  if (valueFormat) {
    if (options) {
      output += format[valueFormat]({ options }).format(value);
    } else {
      output += format[valueFormat]().format(value);
    }
  } else {
    output += value;
  }
  if (options?.suffix) {
    output += `${options.suffix}`;
  }
  if (isNullish(value) && placeholder) {
    return placeholder;
  }
  return output;
};

export {
  coerce,
  parse,
  normalize,
  finalize,
  format,
  patterns,
};
